import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  TablePagination,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Badge,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Tooltip,
  CircularProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  People as UsersIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Block as BlockIcon,
  CheckCircle as ActiveIcon,
  VpnKey as PasswordIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as DateIcon,
  Star as PointsIcon,
  ShoppingCart as OrdersIcon,
  AdminPanelSettings as AdminIcon,
  Person as CustomerIcon,
  Business as WholesaleIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import adminAuthService from '../services/adminAuthService';

const UserManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    address: '',
    role: '',
    points: 0
  });

  // Reset password form state
  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  // Filter and search states
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Statistics
  const [userStats, setUserStats] = useState({
    total: 0,
    active: 0,
    blocked: 0,
    customers: 0,
    wholesale: 0,
    admins: 0
  });

  // Fetch users data
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = adminAuthService.getToken();
      if (!token) {
        throw new Error('رمز التوثيق غير موجود');
      }

      const response = await fetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'فشل في جلب بيانات المستخدمين');
      }

      setUsers(data.users || []);
      calculateStats(data.users || []);
      
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate user statistics
  const calculateStats = (usersData) => {
    const stats = {
      total: usersData.length,
      active: usersData.filter(user => user.status === 'active' || user.active === 1).length,
      blocked: usersData.filter(user => user.status === 'blocked' || user.active === 0).length,
      customers: usersData.filter(user => user.role === 'customer').length,
      wholesale: usersData.filter(user => user.role === 'whole').length,
      admins: usersData.filter(user => user.role === 'admin').length
    };
    setUserStats(stats);
  };

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = users;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(user =>
        user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        user.phone?.includes(searchQuery) ||
        user.id?.toString().includes(searchQuery)
      );
    }

    // Role filter
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter) {
      if (statusFilter === 'active') {
        filtered = filtered.filter(user => user.status === 'active' || user.active === 1);
      } else if (statusFilter === 'blocked') {
        filtered = filtered.filter(user => user.status === 'blocked' || user.active === 0);
      }
    }

    setFilteredUsers(filtered);
    setPage(0); // Reset page when filters change
  }, [users, searchQuery, roleFilter, statusFilter]);

  // Load data on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle user status change
  const handleStatusChange = async (userId) => {
    try {
      setError('');
      setSuccess('');
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'toggle_status',
          user_id: userId
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'فشل في تحديث حالة المستخدم');
      }

      setSuccess('تم تحديث حالة المستخدم بنجاح');
      fetchUsers(); // Refresh data
      
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.message);
    }
  };

  // Handle user role change
  const handleRoleChange = async (userId, newRole) => {
    try {
      setError('');
      setSuccess('');
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: userId,
          role: newRole
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'فشل في تحديث نوع المستخدم');
      }

      setSuccess('تم تحديث نوع المستخدم بنجاح');
      fetchUsers(); // Refresh data
      
    } catch (err) {
      console.error('Error updating user role:', err);
      setError(err.message);
    }
  };

  // Handle user status change (new dropdown version)
  const handleStatusChangeDropdown = async (userId, newStatus) => {
    try {
      setError('');
      setSuccess('');
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: userId,
          status: newStatus,
          active: newStatus === 'active' ? 1 : 0
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'فشل في تحديث حالة المستخدم');
      }

      setSuccess('تم تحديث حالة المستخدم بنجاح');
      fetchUsers(); // Refresh data
      
    } catch (err) {
      console.error('Error updating user status:', err);
      setError(err.message);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError('');
      setSuccess('');
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'delete_user',
          user_id: selectedUser.id
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'فشل في حذف المستخدم');
      }

      setSuccess('تم حذف المستخدم بنجاح');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh data
      
    } catch (err) {
      console.error('Error deleting user:', err);
      setError(err.message);
    }
  };

  // Handle edit user
  const handleEditUser = async () => {
    try {
      setError('');
      setSuccess('');
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: selectedUser.id,
          ...editForm
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'فشل في تحديث بيانات المستخدم');
      }

      setSuccess('تم تحديث بيانات المستخدم بنجاح');
      setEditDialogOpen(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh data
      
    } catch (err) {
      console.error('Error editing user:', err);
      setError(err.message);
    }
  };

  // Handle opening edit dialog
  const handleOpenEditDialog = (user) => {
    setSelectedUser(user);
    setEditForm({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      city: user.city || '',
      address: user.address || '',
      role: user.role || 'customer',
      points: user.points || 0
    });
    setEditDialogOpen(true);
  };

  // Handle reset password
  const handleResetPassword = async () => {
    try {
      setError('');
      setSuccess('');

      // Validate passwords
      if (!resetPasswordForm.newPassword || !resetPasswordForm.confirmPassword) {
        throw new Error('كلمة المرور الجديدة وتأكيدها مطلوبان');
      }

      if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
        throw new Error('كلمة المرور وتأكيدها غير متطابقين');
      }

      if (resetPasswordForm.newPassword.length < 6) {
        throw new Error('كلمة المرور يجب أن تكون 6 أحرف على الأقل');
      }
      
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_users.php`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: selectedUser.id,
          password: resetPasswordForm.newPassword
        })
      });

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'فشل في إعادة تعيين كلمة المرور');
      }

      setSuccess('تم إعادة تعيين كلمة المرور بنجاح');
      setResetPasswordDialogOpen(false);
      setSelectedUser(null);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
      
    } catch (err) {
      console.error('Error resetting password:', err);
      setError(err.message);
    }
  };

  // Handle opening reset password dialog
  const handleOpenResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
    setResetPasswordDialogOpen(true);
  };

  // Get role info for display
  const getRoleInfo = (role) => {
    switch (role) {
      case 'admin':
        return { label: 'مشرف', color: 'error', icon: <AdminIcon fontSize="small" /> };
      case 'whole':
        return { label: 'جملة', color: 'info', icon: <WholesaleIcon fontSize="small" /> };
      case 'customer':
      default:
        return { label: 'عميل', color: 'primary', icon: <CustomerIcon fontSize="small" /> };
    }
  };

  // Get status info for display
  const getStatusInfo = (user) => {
    const isActive = user.status === 'active' || user.active === 1;
    return {
      label: isActive ? 'نشط' : 'محظور',
      color: isActive ? 'success' : 'error',
      icon: isActive ? <ActiveIcon fontSize="small" /> : <BlockIcon fontSize="small" />
    };
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>جاري تحميل بيانات المستخدمين...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#1976d2' }}>
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin')}
            sx={{ mr: { xs: 1, sm: 2 } }}
          >
            <ArrowBackIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
          <UsersIcon sx={{ mr: { xs: 1, sm: 2 }, fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
            {isMobile ? 'المستخدمين' : 'إدارة المستخدمين'}
          </Typography>
          <Button
            color="inherit"
            onClick={fetchUsers}
            startIcon={!isMobile && <RefreshIcon />}
            sx={{ minWidth: { xs: 'auto', sm: '64px' }, px: { xs: 1, sm: 2 } }}
          >
            {isMobile ? <RefreshIcon /> : 'تحديث'}
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3 } }}>
          <Grid item xs={6} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', backgroundColor: '#e3f2fd' }}>
              <CardContent sx={{ py: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 2 } }}>
                <UsersIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: '#1976d2', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1976d2', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {userStats.total}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}>{isMobile ? 'الإجمالي' : 'إجمالي المستخدمين'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', backgroundColor: '#e8f5e8' }}>
              <CardContent sx={{ py: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 2 } }}>
                <ActiveIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: '#2e7d32', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {userStats.active}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}>{isMobile ? 'النشطين' : 'المستخدمين النشطين'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', backgroundColor: '#ffebee' }}>
              <CardContent sx={{ py: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 2 } }}>
                <BlockIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: '#d32f2f', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {userStats.blocked}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}>{isMobile ? 'المحظورين' : 'المستخدمين المحظورين'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', backgroundColor: '#f3e5f5' }}>
              <CardContent sx={{ py: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 2 } }}>
                <CustomerIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: '#7b1fa2', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#7b1fa2', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {userStats.customers}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}>العملاء</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', backgroundColor: '#e1f5fe' }}>
              <CardContent sx={{ py: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 2 } }}>
                <WholesaleIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: '#0277bd', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#0277bd', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {userStats.wholesale}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}>{isMobile ? 'الجملة' : 'تجار الجملة'}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={6} md={2}>
            <Card sx={{ textAlign: 'center', backgroundColor: '#ffebee' }}>
              <CardContent sx={{ py: { xs: 1, sm: 1.5, md: 2 }, px: { xs: 1, sm: 2 } }}>
                <AdminIcon sx={{ fontSize: { xs: 24, sm: 28, md: 32 }, color: '#d32f2f', mb: { xs: 0.5, sm: 1 } }} />
                <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d32f2f', fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
                  {userStats.admins}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}>المشرفين</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filters and Search */}
        <Paper sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="البحث عن مستخدم..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>نوع المستخدم</InputLabel>
                <Select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  label="نوع المستخدم"
                >
                  <MenuItem value="">جميع الأنواع</MenuItem>
                  <MenuItem value="customer">عميل</MenuItem>
                  <MenuItem value="whole">تاجر جملة</MenuItem>
                  <MenuItem value="admin">مشرف</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>الحالة</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="الحالة"
                >
                  <MenuItem value="">جميع الحالات</MenuItem>
                  <MenuItem value="active">نشط</MenuItem>
                  <MenuItem value="blocked">محظور</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setRoleFilter('');
                  setStatusFilter('');
                }}
              >
                مسح الفلاتر
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Users Table */}
        <Paper>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 800, sm: 'auto' } }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>المستخدم</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }}>معلومات الاتصال</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }} align="center">النوع</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }} align="center">الحالة</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }} align="center">النقاط</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }} align="center">تاريخ التسجيل</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' }, px: { xs: 1, sm: 2 } }} align="center">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user) => {
                    const roleInfo = getRoleInfo(user.role);
                    const statusInfo = getStatusInfo(user);
                    
                    return (
                      <TableRow key={user.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              sx={{ 
                                mr: { xs: 1, sm: 2 },
                                width: { xs: 32, sm: 40 },
                                height: { xs: 32, sm: 40 }, 
                                backgroundColor: roleInfo.color === 'error' ? '#d32f2f' : 
                                                roleInfo.color === 'info' ? '#0277bd' : '#7b1fa2'
                              }}
                            >
                              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {user.name || 'غير محدد'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
                                ID: {user.id}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        
                        <TableCell sx={{ px: { xs: 1, sm: 2 } }}>
                          <Stack spacing={0.5}>
                            {user.email && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <EmailIcon sx={{ fontSize: { xs: 14, sm: 18 } }} color="action" />
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{user.email}</Typography>
                              </Box>
                            )}
                            {user.phone && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PhoneIcon sx={{ fontSize: { xs: 14, sm: 18 } }} color="action" />
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{user.phone}</Typography>
                              </Box>
                            )}
                            {user.city && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <LocationIcon sx={{ fontSize: { xs: 14, sm: 18 } }} color="action" />
                                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>{user.city}</Typography>
                              </Box>
                            )}
                          </Stack>
                        </TableCell>
                        
                        <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1, md: 2 } }}>
                          <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 90, md: 100 } }}>
                            <Select
                              value={user.role || 'customer'}
                              onChange={(e) => handleRoleChange(user.id, e.target.value)}
                              disabled={user.role === 'admin'} // Prevent changing admin role
                              sx={{
                                backgroundColor: 
                                  user.role === 'admin' ? '#ffebee' :
                                  user.role === 'whole' ? '#e1f5fe' : '#f3e5f5',
                                '& .MuiSelect-select': {
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                  py: 0.5,
                                  color: 
                                    user.role === 'admin' ? '#d32f2f' :
                                    user.role === 'whole' ? '#0277bd' : '#7b1fa2'
                                }
                              }}
                            >
                              <MenuItem value="customer">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <CustomerIcon fontSize="small" />
                                  عميل
                                </Box>
                              </MenuItem>
                              <MenuItem value="whole">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <WholesaleIcon fontSize="small" />
                                  جملة
                                </Box>
                              </MenuItem>
                              {user.role === 'admin' && (
                                <MenuItem value="admin">
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AdminIcon fontSize="small" />
                                    مشرف
                                  </Box>
                                </MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </TableCell>
                        
                        <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1, md: 2 } }}>
                          <FormControl size="small" sx={{ minWidth: { xs: 80, sm: 90, md: 100 } }}>
                            <Select
                              value={user.status === 'active' || user.active === 1 ? 'active' : 'blocked'}
                              onChange={(e) => handleStatusChangeDropdown(user.id, e.target.value)}
                              disabled={user.role === 'admin'} // Prevent changing admin status
                              sx={{
                                backgroundColor: 
                                  (user.status === 'active' || user.active === 1) ? '#e8f5e8' : '#ffebee',
                                '& .MuiSelect-select': {
                                  fontWeight: 'bold',
                                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                                  py: 0.5,
                                  color: (user.status === 'active' || user.active === 1) ? '#2e7d32' : '#d32f2f'
                                }
                              }}
                            >
                              <MenuItem value="active">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <ActiveIcon fontSize="small" />
                                  نشط
                                </Box>
                              </MenuItem>
                              <MenuItem value="blocked">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <BlockIcon fontSize="small" />
                                  محظور
                                </Box>
                              </MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        
                        <TableCell align="center" sx={{ px: { xs: 1, sm: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            <PointsIcon sx={{ fontSize: { xs: 14, sm: 18 } }} color="warning" />
                            <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                              {user.points || 0}
                            </Typography>
                          </Box>
                        </TableCell>
                        
                        <TableCell align="center" sx={{ px: { xs: 1, sm: 2 } }}>
                          <Typography variant="body2" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.875rem' } }}>
                            {formatDate(user.created_at)}
                          </Typography>
                        </TableCell>
                        
                        <TableCell align="center" sx={{ px: { xs: 0.5, sm: 1, md: 2 } }}>
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap' }}>
                            <Tooltip title="عرض التفاصيل">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDetailsDialogOpen(true);
                                }}
                                sx={{ color: '#1976d2' }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="تعديل">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenEditDialog(user)}
                                sx={{ color: '#ed6c02' }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            <Tooltip title="إعادة تعيين كلمة المرور">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenResetPasswordDialog(user)}
                                sx={{ color: '#9c27b0' }}
                              >
                                <PasswordIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            
                            {user.role !== 'admin' && (
                              <Tooltip title="حذف المستخدم">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedUser(user);
                                    setDeleteDialogOpen(true);
                                  }}
                                  sx={{ color: '#d32f2f' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={filteredUsers.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="عدد الصفوف في الصفحة:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
          />
        </Paper>

        {/* User Details Dialog */}
        <Dialog open={detailsDialogOpen} onClose={() => setDetailsDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ backgroundColor: '#1976d2' }}>
                {selectedUser?.name ? selectedUser.name.charAt(0).toUpperCase() : 'U'}
              </Avatar>
              <Box>
                <Typography variant="h6">{selectedUser?.name || 'غير محدد'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {getRoleInfo(selectedUser?.role).label} - ID: {selectedUser?.id}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>معلومات شخصية</Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">الاسم الكامل</Typography>
                      <Typography>{selectedUser.name || 'غير محدد'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">البريد الإلكتروني</Typography>
                      <Typography>{selectedUser.email || 'غير محدد'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">رقم الهاتف</Typography>
                      <Typography>{selectedUser.phone || 'غير محدد'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">المدينة</Typography>
                      <Typography>{selectedUser.city || 'غير محدد'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">العنوان</Typography>
                      <Typography>{selectedUser.address || 'غير محدد'}</Typography>
                    </Box>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" sx={{ mb: 2, color: '#1976d2' }}>معلومات الحساب</Typography>
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">نوع المستخدم</Typography>
                      <Chip
                        icon={getRoleInfo(selectedUser.role).icon}
                        label={getRoleInfo(selectedUser.role).label}
                        color={getRoleInfo(selectedUser.role).color}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">حالة الحساب</Typography>
                      <Chip
                        icon={getStatusInfo(selectedUser).icon}
                        label={getStatusInfo(selectedUser).label}
                        color={getStatusInfo(selectedUser).color}
                        size="small"
                      />
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">النقاط المكتسبة</Typography>
                      <Typography sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                        {selectedUser.points || 0} نقطة
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">تاريخ التسجيل</Typography>
                      <Typography>{formatDate(selectedUser.created_at)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">آخر تسجيل دخول</Typography>
                      <Typography>{formatDate(selectedUser.last_login) || 'لم يسجل دخول بعد'}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialogOpen(false)}>إغلاق</Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>تعديل بيانات المستخدم</DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="الاسم"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    margin="normal"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="البريد الإلكتروني"
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    margin="normal"
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="رقم الهاتف"
                    value={editForm.phone}
                    onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="المدينة"
                    value={editForm.city}
                    onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="العنوان"
                    value={editForm.address}
                    onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                    margin="normal"
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>نوع المستخدم</InputLabel>
                    <Select
                      value={editForm.role}
                      onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                      label="نوع المستخدم"
                    >
                      <MenuItem value="customer">عميل</MenuItem>
                      <MenuItem value="whole">جملة</MenuItem>
                      {selectedUser.role === 'admin' && <MenuItem value="admin">مشرف</MenuItem>}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="النقاط"
                    type="number"
                    value={editForm.points}
                    onChange={(e) => setEditForm({ ...editForm, points: parseInt(e.target.value) || 0 })}
                    margin="normal"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>إلغاء</Button>
            <Button onClick={handleEditUser} variant="contained" color="primary">
              حفظ التغييرات
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onClose={() => setResetPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>إعادة تعيين كلمة المرور</DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  إعادة تعيين كلمة المرور للمستخدم: <strong>{selectedUser.name}</strong>
                </Typography>
                
                <TextField
                  fullWidth
                  label="كلمة المرور الجديدة"
                  type="password"
                  value={resetPasswordForm.newPassword}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                  margin="normal"
                  required
                  helperText="كلمة المرور يجب أن تكون 6 أحرف على الأقل"
                />
                
                <TextField
                  fullWidth
                  label="تأكيد كلمة المرور"
                  type="password"
                  value={resetPasswordForm.confirmPassword}
                  onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                  margin="normal"
                  required
                  error={resetPasswordForm.confirmPassword && resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword}
                  helperText={
                    resetPasswordForm.confirmPassword && resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword
                      ? "كلمة المرور غير متطابقة"
                      : ""
                  }
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetPasswordDialogOpen(false)}>إلغاء</Button>
            <Button 
              onClick={handleResetPassword} 
              variant="contained" 
              color="primary"
              disabled={
                !resetPasswordForm.newPassword || 
                !resetPasswordForm.confirmPassword || 
                resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword ||
                resetPasswordForm.newPassword.length < 6
              }
            >
              إعادة تعيين كلمة المرور
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>تأكيد حذف المستخدم</DialogTitle>
          <DialogContent>
            <Typography>
              هل أنت متأكد من رغبتك في حذف المستخدم "{selectedUser?.name}"؟
              هذا الإجراء لا يمكن التراجع عنه.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>إلغاء</Button>
            <Button onClick={handleDeleteUser} color="error" variant="contained">
              حذف
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserManagement;
